import axios from "axios";

import { getGuestId } from "@/utils/guestTracking";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

// Attach token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach guest ID for guest tracking
  const guestId = getGuestId();
  config.headers["X-Guest-ID"] = guestId;

  // Add secret header
  config.headers["X-My-Secret"] = "letmein";

  return config;
});

// Refresh token on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Log full error details for debugging
    if (error.response) {
      console.error("❌ API ERROR RESPONSE:", {
        data: error.response.data,
        endpoint: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response.status,
        statusText: error.response.statusText,
      });
      
      // Special logging for validation errors (422)
      if (error.response.status === 422) {
        console.error("🔍 VALIDATION ERRORS (422):", {
          errors: error.response.data?.errors,
          fullResponse: error.response.data,
          message: error.response.data?.message,
        });
      }
    } else if (error.request) {
      console.error("❌ API REQUEST ERROR (No Response):", {
        endpoint: error.config?.url,
        error: error.message,
        method: error.config?.method?.toUpperCase(),
      });
    } else {
      console.error("❌ API ERROR:", error.message);
    }
    
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const refreshRes = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/refresh`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
          },
        );
        const newToken = refreshRes.data.token;
        localStorage.setItem("auth_token", newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem("auth_token");
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export default api;
